import { useEffect, useMemo, useState } from 'react';
import { checkNameError, checkNameNull } from '@/pages/dashboard/components/LeftMenu/utils';
import { LANGUAGE_CN, BUSINESS_TYPE_PERMISSION, PAGE_TYPE_PUBLIC_URL, PAGE_TYPE_COS_URL, PAGE_TYPE_LOOKER, PAGE_TYPE_OUT_PATH, PAGE_TYPE_OUT_URL , PAGE_TYPE_WORKBOOK, PAGE_TYPE_URL, PAGE_TYPE_PATH, PAGE_TYPE_DATALAB, PAGE_TYPE_PRESET, PAGE_URL_KEY, PAGE_TYPE_PDF, PAGE_TYPE_DASHAPP, PAGE_TYPE_AUTH_PATH } from '@/pages/dashboard/_config/constant';
import { getListUserLevel, getTableauList } from '@/pages/dashboard/services/leftMenu';
import { trim } from '@/utils/utils';
import { getDashboardPathType } from '@/pages/dashboard/utils/utils';

// 名字相关，中文名，英文名
export const useNameHooks = (item: any, formatMessage: any, type: string) => {
  const { name_cn: oldNameCN = '', name_en: oldNameEN = '' } = item || {};
  const [nameCN, setNameCN] = useState<string>(oldNameCN); // 英文目录名
  const [nameEN, setNameEN] = useState<string>(oldNameEN); // 中文目录名
  // 英文目录名错误信息
  const [nameENErr, setNameENErr] = useState<string>('');
  // 中文目录名错误信息
  const [nameCNErr, setNameCNErr] = useState('');

  // 框架报表，只有在点击确认时才有名称的校验
  const [namePathENErr, setNamePathENErr] = useState<string>('');

  const menuErrorInfoId = type === "folder" || type === "subfolder" ? 'folder_info_name_err' : 'menu_info_name_err';

  // 检测英文目录名
  const checkNameEN: () => boolean = () => {
    // 目录名为空字符串的情况
    if (checkNameNull(nameEN)) {
      const errorTipsObj: any = {
        dashboard: 'menu_dashboard_name_null',
        folder: 'menu_folder_name_null',
        subfolder: 'menu_subfolder_name_null',
        menu: 'menu_menu_name_null',
      };
      // const errorTips = 'menu_dashboard_name_null';
      setNameENErr(formatMessage({ id: errorTipsObj?.[type] }));
      return false;
    }

    if (nameEN?.replace(/\s/g, "")?.length > 50) {
      setNameENErr(formatMessage({ id: 'menu_name_max_length_error' }));
      return false;
    }
    if (checkNameError(nameEN,type)) {
      setNameENErr(formatMessage({ id: menuErrorInfoId }));
      return false;
    }
    setNameENErr('');
    return true;
  };

  // 检测中文目录名,中文目录名可以为空
  const checkNameCN: () => boolean = () => {
    if (nameCN && checkNameError(nameCN,type)) {
      setNameCNErr(formatMessage({ id: menuErrorInfoId }));
      return false;
    }

    if (nameCN?.replace(/\s/g, "")?.length > 50) {
      setNameCNErr(formatMessage({ id: 'menu_name_max_length_error' }));
      return false;
    }

    setNameCNErr('');
    return true;
  };

  return {
    nameCN,
    setNameCN,
    nameEN,
    setNameEN,
    nameENErr,
    setNameENErr,
    nameCNErr,
    setNameCNErr,
    checkNameEN,
    checkNameCN,
    namePathENErr, setNamePathENErr
  };
};

// 菜单相关
export const useMenuHooks = (props: any) => {
  const { currentGame, menuList, locale, formatMessage } = props;
  const [selectMenu, setSelectMenu] = useState<string>('');
  // 菜单错误信息
  const [menuErr, setMenuErr] = useState<string>('');

  const { filterMenuList } = useFilterMenu({ currentGame, menuList, locale });

  // 检测菜单是否选中
  const checkMenu: () => boolean = () => {
    // 目录名为空字符串的情况
    if (!selectMenu) {
      setMenuErr(formatMessage({ id: 'menu_select_null' }));
      return false;
    }
    setMenuErr('');
    return true;
  };

  return {
    selectMenu,
    setSelectMenu,
    menuErr,
    setMenuErr,
    formatMenuList: filterMenuList,
    checkMenu
  };
};

// 筛选menu
export const useFilterMenu = (props: any) => {
  const { currentGame, menuList, locale } = props;
  const filterMenuList = useMemo(() => {
    const nameKey = locale === LANGUAGE_CN ? 'name_cn' : 'name_en';
    const { role_id: roleId = '' } = currentGame || {};
    return (menuList || []).filter((menu: any) => 
      // @ts-ignore
      (BUSINESS_TYPE_PERMISSION[roleId] || BUSINESS_TYPE_PERMISSION.default).includes(menu?.business_type)
    ).map((menu: any) => {
      const value = menu.id;
      const text = menu[nameKey] || menu?.name_en;
      return {
        ...menu,
        value,
        text
      };
    });
  }, [locale, menuList, currentGame]);
  return {
    filterMenuList
  };
};

// 筛选folder
export const useFilterFolder = (props: any) => {
  const { folderList, menuId, locale } = props;
  const filterFolderList = useMemo(() => {
    const nameKey = locale === LANGUAGE_CN ? 'name_cn' : 'name_en';
    return (folderList || []).filter((folder: any) => folder.parent_id === menuId).map((folder: any) => {
      const value = folder.id;
      const text = folder[nameKey] || folder?.name_en;
      return {
        ...folder,
        value,
        text
      };
    });
  }, [locale, folderList, menuId]);
  return {
    filterFolderList
  };
};

// 目录相关
export const useFolderHooks = (props: any) => {
  const { folderList, selectMenu, locale, formatMessage } = props;
  const [selectFolder, setSelectFolder] = useState<string>('');
  // 目录错误信息
  const [folderErr, setFolderErr] = useState<string>('');
  const formatFolderList = useMemo(() => {
    if (!selectMenu) {
      return [];
    }
    return (folderList || []).filter((folder: any) => folder.parent_id === selectMenu).map((folder: any) => {
      const nameKey = locale === LANGUAGE_CN ? 'name_cn' : 'name_en';
      const value = folder.id;
      const text = folder[nameKey] || folder?.name_en;
      return {
        ...folder,
        value,
        text
      };
    });
  }, [folderList, selectMenu, locale]);

  // menu 更改重置  folder选择
  useEffect(() => {
    setSelectFolder('');
  }, [selectMenu]);

  // 检测目录是否选中
  const checkFolder: () => boolean = () => {
    // 目录名为空字符串的情况
    if (!selectFolder) {
      setFolderErr(formatMessage({ id: 'menu_folder_select_null' }));
      return false;
    }
    setFolderErr('');
    return true;
  };

  return {
    selectFolder,
    setSelectFolder,
    folderErr,
    setFolderErr,
    formatFolderList,
    checkFolder
  };
};

// 目录相关
export const useSubFolderHooks = (props: any) => {
  const { subFolderList, selectFolder, locale, formatMessage } = props;
  const [selectSubFolder, setSelectSubFolder] = useState<string>('');
  // 目录错误信息
  const [subFolderErr, setSubFolderErr] = useState<string>('');

  const formatSubFolderList = useMemo(() => {
    if (!selectFolder) {
      return [];
    }
    return (subFolderList || []).filter((folder: any) => folder.parent_id === selectFolder).map((folder: any) => {
      const nameKey = locale === LANGUAGE_CN ? 'name_cn' : 'name_en';
      const value = folder.id;
      const text = folder[nameKey] || folder?.name_en;
      return {
        ...folder,
        value,
        text
      };
    });
  }, [subFolderList, selectFolder, locale]);

  // folder 更改重置  subFolder选择
  useEffect(() => {
    setSelectSubFolder('');
  }, [selectFolder]);

  // 检测目录是否选中
  const checkSubFolder: () => boolean = () => {
    // 目录名为空字符串的情况
    if (!selectSubFolder) {
      setSubFolderErr(formatMessage({ id: 'menu_subfolder_select_null' }));
      return false;
    }
    setSubFolderErr('');
    return true;
  };

  return {
    selectSubFolder,
    setSelectSubFolder,
    subFolderErr,
    setSubFolderErr,
    formatSubFolderList,
    checkSubFolder
  };
};

// userlevel相关
export const useListUserLevel = (props: any) => {
  const { currentGame, userInfo, formatMessage, item } = props;
  const [userLevelList, setUserLevelList] = useState<Array<any>>([]);
  const [selectedLevel, setSelectedLevel] = useState<string | undefined>(item?.level);
  const [userLevelErr, setUserLevelErr] = useState<string>('');
  const { game_code: gameCode, studio, third_party_game: thirdPartyGame } = currentGame || {};
  // 获取userList列表
  const getUserLevelList = () => {
    getListUserLevel({
      user_id: userInfo?.userid || '',
      game_code: gameCode,
      studio_code: studio,
      third_party_game: thirdPartyGame
    }).then((data: any) => {
      const options = [] as any;
      if (data) {
        Object.keys(data).forEach(key => {
          options.push({
            value: key,
            text: key,
            des: data[key]
          });
        });
      }      
      setUserLevelList(options);
    }, () => {
      setUserLevelList([]);
    }).catch(e => {
      console.log(e);
    });
  };

  // 检测userlevel是否选中
  const checkUserLevel: () => boolean = () => {
    // 目录名为空字符串的情况
    if (!selectedLevel) {
      setUserLevelErr(formatMessage({ id: 'menu_user_level_select_null' }));
      return false;
    }
    setUserLevelErr('');
    return true;
  };

  useEffect(() => {
    if (currentGame && userInfo) {
      getUserLevelList();
    }
  }, [currentGame, userInfo]);

  return {
    userLevelList,
    selectedLevel,
    setSelectedLevel,
    userLevelErr,
    setUserLevelErr,
    checkUserLevel
  };
};

interface PathInfoType {
  path: string | undefined;  // 自建报表
  workbookUrl: string | undefined;  // tableau 报表
  url: string; // tableau url报表
  dashboardUUID: string; // datalab报表
  presetId: string; // preset报表
  pdf: string; // pdf报表
  dashApp: string; // dashApp 报表
  publicUrl: string;
  cosUrl: string;
  looker:string;
  outUrl: string;
  outPath: string;
  authPath: string;
}

interface UsePathPropsType {
  formatMessage: any;
  userInfo: any;
  currentGame: any;
  item: any;
  setNamePathENErr: any;
  pdfFileUploadStatus: string;
}

interface IPresetExtInfo {
  workspaceId: string;
  workspaceRegion: string;
  teamId: string;
  extraInfo?:string;// 报表额外信息
}

interface LookerExtInfo {
  lookerModels: string;
}

const initPathInfo: PathInfoType = {
  path: undefined,
  workbookUrl: undefined,
  url: '',
  dashboardUUID: '',
  presetId: '',
  pdf: '',
  dashApp: '',
  publicUrl: '',
  looker: '',
  cosUrl: '',
  outUrl: '',
  outPath: '',
  authPath: ''
};

const initPresetExtInfo: IPresetExtInfo = {
  workspaceId: '',
  workspaceRegion: '',
  teamId: ''
};

const initLookerExtInfo: LookerExtInfo = {
  lookerModels: '',

};

// path 相关
export const usePath = (props: UsePathPropsType) => {
  const { formatMessage, userInfo, currentGame, item, setNamePathENErr, pdfFileUploadStatus } = props;
  const [selectedPath, setSelectedPath] = useState<string>(getDashboardPathType(item));
  const [tableauList, setTableauList] = useState([]);
  const isPdf = item?.page_type === PAGE_TYPE_PDF;
  // pdf类型和url类型，共用后端字段url
  const isDashApp = item?.page_type === PAGE_TYPE_DASHAPP;
  const isPublicUrl = item?.page_type === PAGE_TYPE_PUBLIC_URL;
  const isCosUrl = item?.page_type === PAGE_TYPE_COS_URL;
  const islooker = item?.page_type === PAGE_TYPE_LOOKER;
  const isOutUrl = item?.page_type === PAGE_TYPE_OUT_URL;
  const isAuthPath = item?.page_type === PAGE_TYPE_AUTH_PATH;
  const isOutPath = item?.page_type === PAGE_TYPE_OUT_PATH;
  // const { preset_id: fullPresetId = '' } = item || {};
  // const [ presetId = '', workspaceId = '', workspaceRegion = '', teamId = '' ] = fullPresetId.split(';');

  const { workspace_id: workspaceId = '', workspace_region: workspaceRegion = '', team_id: teamId = '', preset_id: presetId, extra_info: extraInfo = '' } = item || {};
  const { looker_models: lookerModels = ''} = item || {};

  const [pathInfo, setPathInfo] = useState<PathInfoType>(item ? {
    path: item?.[PAGE_URL_KEY[PAGE_TYPE_PATH]],
    workbookUrl: item?.[PAGE_URL_KEY[PAGE_TYPE_WORKBOOK]],
    url: !isPdf && !isDashApp ? item?.[PAGE_URL_KEY[PAGE_TYPE_URL]] : '',
    dashboardUUID: item?.[PAGE_URL_KEY[PAGE_TYPE_DATALAB]],
    presetId,
    pdf: isPdf ? item?.[PAGE_URL_KEY[PAGE_TYPE_URL]] : '',
    dashApp: isDashApp ? item?.[PAGE_URL_KEY[PAGE_TYPE_URL]] : '',
    publicUrl: isPublicUrl ? item?.[PAGE_URL_KEY[PAGE_TYPE_URL]] : '',
    cosUrl: isCosUrl ? item?.[PAGE_URL_KEY[PAGE_TYPE_URL]] : '',
    looker: islooker ? item?.[PAGE_URL_KEY[PAGE_TYPE_URL]] : '',
    authPath: isAuthPath ? item?.[PAGE_URL_KEY[PAGE_TYPE_AUTH_PATH]] : '',
    outUrl: isOutUrl ? item?.[PAGE_URL_KEY[PAGE_TYPE_URL]] : '',
    outPath: isOutPath ? item?.[PAGE_URL_KEY[PAGE_TYPE_URL]] : ''
  } : initPathInfo);
  const [pathInfoErr, setPathInfoErr] = useState<string>('');

  // const { workspace_id: workspaceId = '', workspace_region: workspaceRegion = '', team_id: teamId = '' } = item || {};
  const [presetExtInfo, setPresetExtInfo] = useState<IPresetExtInfo>(item ? {
    workspaceId,
    workspaceRegion,
    teamId,
    extraInfo,
  } : initPresetExtInfo);
  const [presetExtInfoErr, setPresetExtInfoErr] = useState<IPresetExtInfo>(initPresetExtInfo);

  // looker
  const [lookerExtInfo, setLookerExtInfo] = useState<LookerExtInfo>(item ? { 
    lookerModels 
  } : initLookerExtInfo);
  const [lookerExtInfoErr, setLookerExtInfoErr] = useState<LookerExtInfo>(initLookerExtInfo);

  // 获取tableau列表
  const doGetTableauList = () => {
    getTableauList({
      user_id: userInfo?.userid || '',
      game_code: currentGame?.game_code,
      studio_code: currentGame?.studio,
      menu_type: item?.business_type === 'other' ? 'features' : item?.business_type?.toLowerCase() || '',
    }).then((data: any) => {
      setTableauList(data ?? []);
    }, () => {
      setTableauList([]);
    });
  };

  useEffect(() => {
    if (currentGame && userInfo && selectedPath === PAGE_TYPE_WORKBOOK) {
      doGetTableauList();
    }
  }, [currentGame, userInfo]);

  const checkPathType: () => boolean = () => {
    const { path, workbookUrl, url, dashboardUUID, presetId, pdf, dashApp, publicUrl, cosUrl,looker, outUrl, outPath, authPath } = pathInfo;
    const patternId = /^[a-zA-Z0-9_-]+$/;
    // let patterneUrl = /^https:\/\/(([a-zA-Z0-9_-])+(\.)?)*\/#(\/site\/[\w-]+)?\/views(\/[\w-]+)+([\w-./?%&:=])+/
    // 上面正则导致页面卡死：如：https://us-funcom-tableau-public.intlgame.com/#/views/dauchangepointdetectiondemo/DAU_dashboard?:iid=1
    // https://us-funcom-tableau-public.intlgame.com/views/dauchangepointdetectiondemo/DAU_dashboard?:iid=1
    const patterneUrl = /^https:\/\/.*\/#\/site\/.*\/views\/.*$/;
    const patterneUrl1 = /^https:\/\/.*\/#\/views(\/[\w-]+)+([\w-./?%&:=]*)?$/;

    const urlRegex = new RegExp('^((https:\\/\\/)' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name and extension
      '((\\d{1,3}\\.){3}\\d{1,3})))?' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$','i'); // fragment locator

    const pdfUrlId = /^https?:\/\/.*\.pdf(\?[^\s]*)?(#[^\s]*)?$/i;
    const isValidAuthPath = (value: string) => {
      const normalized = trim(value);
      if (!normalized) return false;
      // auth_path only supports relative path style value
      if (/^https?:\/\//i.test(normalized) || /^\/\//.test(normalized)) {
        return false;
      }
      if (normalized.startsWith('/')) {
        return true;
      }
      return /\.html?(\?.*)?$/i.test(normalized);
    };

    if (selectedPath === PAGE_TYPE_WORKBOOK) {
      if (!workbookUrl) {
        setPathInfoErr(formatMessage({ id: 'menu_path_webhook_url_select_null' }));
        return false;
      }
    }
    if (selectedPath === PAGE_TYPE_PATH) {
      if (!path) {
        setPathInfoErr(formatMessage({ id: 'menu_path_path_select_null' }));
        return false;
      }
    }
    if (selectedPath === PAGE_TYPE_DATALAB) {
      if (!trim(dashboardUUID)) {
        setPathInfoErr(formatMessage({ id: 'menu_path_datalab_null' }));
        return false;
      }
      if (!patternId.test(dashboardUUID)) {
        setPathInfoErr(formatMessage({ id: 'menu_path_datalab_err' }));
        return false;
      }
    }
    if (selectedPath === PAGE_TYPE_PRESET) {
      if (!trim(presetId)) {
        setPathInfoErr(formatMessage({ id: 'menu_path_preset_null' }));
        return false;
      }
      if (!patternId.test(presetId)) {
        setPathInfoErr(formatMessage({ id: 'menu_path_preset_err' }));
        return false;
      }
    }
    if (selectedPath === PAGE_TYPE_PDF) {
      if (!trim(pdf)) {
        setPathInfoErr(formatMessage({ id: 'menu_pdf_url_null' }));
        return false;
      }
      if (!pdfUrlId.test(pdf)) {
        setPathInfoErr(pdfFileUploadStatus === "success" ? 
          formatMessage({ id: 'menu_pdf_uploaded_err' }) 
          : 
          formatMessage({ id: 'menu_pdf_url_err' }));
        return false;
      }
    }
    if (selectedPath === PAGE_TYPE_URL) {
      if (!trim(url)) {
        setPathInfoErr(formatMessage({ id: 'menu_path_url_null' }));
        return false;
      }
      if (!patterneUrl.test(url) && !patterneUrl1.test(url)) {
        setPathInfoErr(formatMessage({ id: 'menu_path_url_err' }));
        return false;
      }
    }
    if (selectedPath === PAGE_TYPE_DASHAPP) {
      if (!trim(dashApp)) {
        setPathInfoErr(formatMessage({ id: 'menu_dashApp_url_null' }));
        return false;
      }
      if (!urlRegex.test(dashApp)) {
        setPathInfoErr(formatMessage({ id: 'menu_dashApp_url_err' }));
        return false;
      }
    }
    if (selectedPath === PAGE_TYPE_PUBLIC_URL) {
      if (!trim(publicUrl)) {
        setPathInfoErr(formatMessage({ id: 'menu_path_url_null' }));
        return false;
      }
      if (!urlRegex.test(publicUrl)) {
        setPathInfoErr(formatMessage({ id: 'menu_path_url_err' }));
        return false;
      }
    }
    if (selectedPath === PAGE_TYPE_COS_URL) {
      if (!trim(cosUrl)) {
        setPathInfoErr(formatMessage({ id: 'menu_cos_url_null' }));
        return false;
      }
      // if (!htmlUrlId.test(cosUrl)) {
      //   setPathInfoErr(formatMessage({ id: 'menu_cos_url_err' }));
      //   return false;
      // }
    }
    if (selectedPath === PAGE_TYPE_LOOKER) {
      if (!trim(looker)) {
        setPathInfoErr(formatMessage({ id: 'menu_looker_url_null' }));
        return false;
      }
      if (!urlRegex.test(looker)) {
        setPathInfoErr(formatMessage({ id: 'menu_looker_url_err' }));
        return false;
      }
    }
    if (selectedPath === PAGE_TYPE_OUT_URL) {
      if (!trim(outUrl)) {
        setPathInfoErr(formatMessage({ id: 'menu_path_url_null' }));
        return false;
      }
      // if (!urlRegex.test(outUrl)) {
      //   setPathInfoErr(formatMessage({ id: 'menu_path_url_err' }));
      //   return false;
      // }
    }
    if (selectedPath === PAGE_TYPE_OUT_PATH) {
      if (!trim(outPath)) {
        setPathInfoErr(formatMessage({ id: 'menu_path_url_null' }));
        return false;
      }
    }
    if (selectedPath === PAGE_TYPE_AUTH_PATH) {
      if (!trim(authPath)) {
        setPathInfoErr(formatMessage({ id: 'menu_auth_path_null' }));
        return false;
      }
      if (!isValidAuthPath(authPath)) {
        setPathInfoErr(formatMessage({ id: 'menu_auth_path_err' }));
        return false;
      }
    }
    return true;
  };

  const setPathStateInfo: (params?: Record<string, any>) => void = (params = {}) => {
    setPathInfo({
      ...pathInfo,
      ...params
    });
    setPathInfoErr('');
  };

  const setSelectedPathState = (val: string) => {
    if (currentGame && userInfo && val === PAGE_TYPE_WORKBOOK) {
      doGetTableauList();
    }
    setSelectedPath(val);
    setPathInfoErr('');
    setNamePathENErr('');
  };

  // 组装path数据
  const getPathInfo = (init = initPathInfo) => {
    const { path, workbookUrl, url, dashboardUUID, presetId } = init;
    let urlVal = '';
    let pathVal = (selectedPath === PAGE_TYPE_PATH ? pathInfo.path : path) || '';
    if (selectedPath === PAGE_TYPE_URL) {
      urlVal = pathInfo.url;
    } else if (selectedPath === PAGE_TYPE_PDF) {
      urlVal = pathInfo.pdf;
    } else if (selectedPath === PAGE_TYPE_DASHAPP) {
      urlVal = pathInfo.dashApp;
    } else if (selectedPath === PAGE_TYPE_PUBLIC_URL) {
      urlVal = pathInfo.publicUrl;
    } else if (selectedPath === PAGE_TYPE_COS_URL) {
      urlVal = pathInfo.cosUrl;
    } else if (selectedPath === PAGE_TYPE_LOOKER) {
      urlVal = pathInfo.looker;
    }else if (selectedPath === PAGE_TYPE_OUT_URL) {
      urlVal = pathInfo.outUrl;
    }else if (selectedPath === PAGE_TYPE_OUT_PATH) {
      urlVal = pathInfo.outPath;
    }else if(selectedPath === PAGE_TYPE_AUTH_PATH) {
      urlVal = '';
      pathVal = pathInfo.authPath || '';
    }else {
      urlVal = url;
    }
    return {
      path: pathVal,
      url: urlVal || '',
      workbook_url: (selectedPath === PAGE_TYPE_WORKBOOK ? pathInfo.workbookUrl : workbookUrl) || '',
      dashboard_uuid: (selectedPath === PAGE_TYPE_DATALAB ? pathInfo.dashboardUUID : dashboardUUID) || '',
      preset_id: (selectedPath === PAGE_TYPE_PRESET ? pathInfo.presetId : presetId) || '',
    };
  };

  const checkPresetExtInfo: () => boolean = () => {
    const { workspaceId, workspaceRegion, teamId } = presetExtInfo;
    let workspaceIdErr = '';
    let workspaceRegionErr = '';
    let teamIdErr = '';
    const patterneUrl = /^([a-zA-Z0-9_-])+$/;

    if (selectedPath !== PAGE_TYPE_PRESET) {
      return true;
    }

    if (!workspaceId && !workspaceRegion && !teamId) {
      setPresetExtInfoErr({
        workspaceId: workspaceIdErr,
        workspaceRegion: workspaceRegionErr,
        teamId: teamIdErr
      });
      return true;
    }

    if (!patterneUrl.test(workspaceId)) {
      workspaceIdErr = formatMessage({ id: 'preset_ext_info_workspace_id_err' });
    }

    if (!patterneUrl.test(workspaceRegion)) {
      workspaceRegionErr = formatMessage({ id: 'preset_ext_info_workspace_region_err' });
    }

    if (!patterneUrl.test(teamId)) {
      teamIdErr = formatMessage({ id: 'preset_ext_info_team_id_err' });
    }
    setPresetExtInfoErr({
      workspaceId: workspaceIdErr,
      workspaceRegion: workspaceRegionErr,
      teamId: teamIdErr
    });
    if (workspaceIdErr || workspaceRegionErr || teamIdErr) {
      return false;
    }
    return true;
  };

  const setPresetExtStateInfo: () => void = (params = {}) => {
    setPresetExtInfo({
      ...presetExtInfo,
      ...params
    });
    setPathInfoErr('');
  };

  const getPresetExtInfo = () => {
    if (selectedPath !== PAGE_TYPE_PRESET) {
      return {};
    }
    const { workspaceId, workspaceRegion, teamId, extraInfo } = presetExtInfo;
    return {
      workspace_id: workspaceId,
      workspace_region: workspaceRegion,
      team_id: teamId,
      extra_Info: extraInfo
    };
  };


  const checkLookerExtInfo: () => boolean = () => {
    const { lookerModels,} = lookerExtInfo;
    let modelsErr = '';
    const patternModels = /^[a-zA-Z0-9_,-]+$/;

    if (selectedPath !== PAGE_TYPE_LOOKER) {
      return true;
    }

    if (!lookerModels) {
      modelsErr = formatMessage({ id: 'menu_looker_models_null' });
      setLookerExtInfoErr({
        lookerModels: modelsErr,
      });
      return false;
    }

    if (!patternModels.test(lookerModels)) {
      modelsErr = formatMessage({ id: 'looker_ext_info_models_err' });
    }

    setLookerExtInfoErr({
      lookerModels: modelsErr,

    });
    if (modelsErr) {
      return false;
    }
    return true;
  };

  const setLookerExtStateInfo: () => void = (params = {}) => {
    setLookerExtInfo({
      ...lookerExtInfo,
      ...params
    });
    setPathInfoErr('');
  };

  const getLookerExtInfo = () => {
    if (selectedPath !== PAGE_TYPE_LOOKER) {
      return {};
    }
    const { lookerModels  } = lookerExtInfo;
    return {
      looker_models: lookerModels,

    };
  };
  return {
    selectedPath,
    setSelectedPath: setSelectedPathState,
    pathInfo,
    setPathStateInfo,
    getPathInfo,
    pathInfoErr,
    setPathInfoErr,
    tableauList,
    checkPathType,
    presetExtInfo,
    setPresetExtStateInfo,
    presetExtInfoErr,
    checkPresetExtInfo,
    getPresetExtInfo,
    lookerExtInfo,
    lookerExtInfoErr,
    checkLookerExtInfo,
    setLookerExtStateInfo,
    getLookerExtInfo,
  };
};


export const useNull = (selectedLevel: string | undefined, nameEN: string, selectMenu: string | undefined, selectFolder: string | undefined, selectedPath: string, getPathInfo: any, type: string) => {
  const urlObj: any = getPathInfo();
  const isNull = useMemo(() => {
    const urlObjIsNull = urlObj?.[selectedPath] === '';
    const isAdd = type === 'add';
    return isAdd ? !selectedLevel || !nameEN || !selectMenu || !selectFolder || urlObjIsNull
      : !nameEN || !selectedLevel || urlObjIsNull;
  }, [urlObj, selectedPath, type, selectedLevel, nameEN, selectMenu, selectFolder]);
  return {
    isNull
  };
};
